import React, { useEffect, useState } from "react";
import { Icon } from "@iconify/react/dist/iconify.js";
import { toast, ToastContainer } from "react-toastify";
import { useTranslation } from "../i18n/I18nContext";
import { listUniforms, createUniform } from "../services/uniform";
import WizardSteps from "./shared/WizardSteps";

const TOTAL_STEPS = 2;
const GENERO_OPTIONS = ["Masculino", "Feminino", "Unissexo"];

const initialState = {
  artigo: "",
  genero: "Unissexo",
  tamanho: "",
  cor: "",
  preco: "",
  quantidade: "",
};

const CadastrarArtigosLayer = () => {
  const { t } = useTranslation();
  const [currentStep, setCurrentStep] = useState(1);
  const [form, setForm] = useState(initialState);
  const [uniforms, setUniforms] = useState([]);
  const [loading, setLoading] = useState(true);
  const [submitting, setSubmitting] = useState(false);
  const [done, setDone] = useState(false);

  const stepLabels = [t("artigo.wizard.step.data"), t("artigo.wizard.step.confirm")];

  const fetchUniforms = () => {
    setLoading(true);
    listUniforms()
      .then((resp) => setUniforms(resp.data || []))
      .catch(() => toast.error(t("artigo.error.load")))
      .finally(() => setLoading(false));
  };

  useEffect(fetchUniforms, [t]);

  const setField = (field) => (e) => setForm((f) => ({ ...f, [field]: e.target.value }));

  const nextStep = () => {
    if (!form.artigo || !form.tamanho) {
      toast.error(t("artigo.error.requiredFields"));
      return;
    }
    if (currentStep < TOTAL_STEPS) setCurrentStep(currentStep + 1);
  };
  const prevStep = () => {
    if (currentStep > 1) setCurrentStep(currentStep - 1);
  };
  const restart = () => {
    setForm(initialState);
    setCurrentStep(1);
    setDone(false);
  };

  const handleSubmit = async () => {
    setSubmitting(true);
    try {
      await createUniform({
        artigo: form.artigo,
        genero: form.genero,
        tamanho: form.tamanho,
        cor: form.cor,
        preco: form.preco ? Number(form.preco) : 0,
        quantidade: form.quantidade ? Number(form.quantidade) : 0,
      });
      setDone(true);
      fetchUniforms();
    } catch (err) {
      toast.error(err?.response?.data?.error || t("artigo.error.create"));
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <div className='row gy-4'>
      <ToastContainer />

      <div className='col-12'>
        <div className='card'>
          <div className='card-body'>
            <h6 className='mb-4 text-xl'>{t("artigo.wizard.title")}</h6>
            <p className='text-neutral-500'>{t("artigo.wizard.subtitle")}</p>

            <div className='form-wizard'>
              <WizardSteps steps={stepLabels} currentStep={done ? TOTAL_STEPS + 1 : currentStep} />

              {!done && currentStep === 1 && (
                <fieldset className='wizard-fieldset show'>
                  <div className='row gy-3'>
                    <div className='col-sm-6'>
                      <label className='form-label'>{t("artigo.field.name")}</label>
                      <div className="icon-field">
                        <span className="icon">
                          <Icon icon="f7:person" />
                        </span>
                        <input type='text' className='form-control' value={form.artigo} onChange={setField("artigo")} required />
                      </div>
                    </div>
                    <div className='col-sm-3'>
                      <label className='form-label'>{t("artigo.field.gender")}</label>
                      <select className='form-select' value={form.genero} onChange={setField("genero")}>
                        {GENERO_OPTIONS.map((g) => (
                          <option key={g} value={g}>
                            {g}
                          </option>
                        ))}
                      </select>
                    </div>
                    <div className='col-sm-3'>
                      <label className='form-label'>{t("artigo.field.size")}</label>
                      <div className="icon-field">
                        <span className="icon">
                          <Icon icon="solar:pen-2-outline" />
                        </span>
                        <input type='text' className='form-control' value={form.tamanho} onChange={setField("tamanho")} required />
                      </div>
                    </div>
                    <div className='col-sm-4'>
                      <label className='form-label'>{t("artigo.field.color")}</label>
                      <div className="icon-field">
                        <span className="icon">
                          <Icon icon="solar:pen-2-outline" />
                        </span>
                        <input type='text' className='form-control' value={form.cor} onChange={setField("cor")} />
                      </div>
                    </div>
                    <div className='col-sm-4'>
                      <label className='form-label'>{t("artigo.field.price")}</label>
                      <div className="icon-field">
                        <span className="icon">
                          <Icon icon="solar:dollar-minimalistic-outline" />
                        </span>
                        <input type='number' min='0' className='form-control' value={form.preco} onChange={setField("preco")} />
                      </div>
                    </div>
                    <div className='col-sm-4'>
                      <label className='form-label'>{t("artigo.field.stock")}</label>
                      <div className="icon-field">
                        <span className="icon">
                          <Icon icon="solar:box-minimalistic-outline" />
                        </span>
                        <input type='number' min='0' className='form-control' value={form.quantidade} onChange={setField("quantidade")} />
                      </div>
                    </div>
                  </div>
                  <div className='form-group d-flex align-items-center justify-content-end mt-24'>
                    <button type='button' className='form-wizard-next-btn btn btn-primary-600 px-32' onClick={nextStep}>
                      {t("global.next")} <i className='ri-arrow-right-line' />
                    </button>
                  </div>
                </fieldset>
              )}

              {!done && currentStep === 2 && (
                <fieldset className='wizard-fieldset show'>
                  <div className='alert alert-info'>{t("artigo.confirm.description")}</div>
                  <ul className='list-unstyled d-flex flex-column gap-8'>
                    <li>
                      <strong>{t("artigo.field.name")}:</strong> {form.artigo}
                    </li>
                    <li>
                      <strong>{t("artigo.field.gender")}:</strong> {form.genero}
                    </li>
                    <li>
                      <strong>{t("artigo.field.size")}:</strong> {form.tamanho}
                    </li>
                    <li>
                      <strong>{t("artigo.field.price")}:</strong> {form.preco || 0}
                    </li>
                    <li>
                      <strong>{t("artigo.field.stock")}:</strong> {form.quantidade || 0}
                    </li>
                  </ul>
                  <div className='form-group d-flex align-items-center justify-content-end gap-8 mt-24'>
                    <button
                      type='button'
                      className='form-wizard-previous-btn btn btn-neutral-500 border-neutral-100 px-32'
                      onClick={prevStep}
                      disabled={submitting}
                    >
                      {t("global.previous")}
                    </button>
                    <button type='button' className='form-wizard-submit btn btn-primary-600 px-32' onClick={handleSubmit} disabled={submitting}>
                      {submitting ? (
                        t("global.loading")
                      ) : (
                        <>
                          <i className='ri-check-line' /> {t("global.confirm")}
                        </>
                      )}
                    </button>
                  </div>
                </fieldset>
              )}

              {done && (
                <fieldset className='wizard-fieldset show'>
                  <div className='text-center mb-40'>
                    <span className='w-64-px h-64-px bg-success-focus text-success-main rounded-circle d-inline-flex justify-content-center align-items-center mb-16 text-2xl'>
                      <i className='ri-check-line' />
                    </span>
                    <h6 className='text-md text-neutral-600'>{t("artigo.success.created")}</h6>
                  </div>
                  <div className='form-group d-flex align-items-center justify-content-center gap-8'>
                    <button type='button' className='btn btn-neutral-500 border-neutral-100 px-32' onClick={restart}>
                      {t("artigo.action.createAnother")}
                    </button>
                  </div>
                </fieldset>
              )}
            </div>
          </div>
        </div>
      </div>

      <div className='col-12'>
        <div className='card radius-12'>
          <div className='card-body'>
            <h6 className='mb-16'>{t("artigo.section.list")}</h6>
            {loading ? (
              <div className='text-center py-40'>{t("global.loading")}</div>
            ) : uniforms.length === 0 ? (
              <div className='alert alert-info mb-0'>{t("artigo.hint.noArticles")}</div>
            ) : (
              <div className='table-responsive'>
                <table className='table bordered-table mb-0'>
                  <thead>
                    <tr>
                      <th>{t("artigo.field.name")}</th>
                      <th>{t("artigo.field.gender")}</th>
                      <th>{t("artigo.field.size")}</th>
                      <th>{t("artigo.field.color")}</th>
                      <th>{t("artigo.field.price")}</th>
                      <th>{t("artigo.col.available")}</th>
                      <th>{t("artigo.col.sold")}</th>
                    </tr>
                  </thead>
                  <tbody>
                    {uniforms.map((u) => (
                      <tr key={u.id}>
                        <td>{u.artigo}</td>
                        <td>{u.genero}</td>
                        <td>{u.tamanho}</td>
                        <td>{u.cor || "-"}</td>
                        <td>{u.preco || 0}</td>
                        <td>{u.quantidade || 0}</td>
                        <td>{u.quantidadeVendida || 0}</td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            )}
          </div>
        </div>
      </div>
    </div>
  );
};

export default CadastrarArtigosLayer;
